import { useSearchParams } from 'react-router';

import { Loading } from '@/components/Loading';
import { LoginForm } from '@/forms/LoginForm/LoginForm';
import { AuthLayout } from '@/layouts/AuthLayout';
import { useAuth } from '@/lib/auth';
import { trackEvent } from '@/lib/analytics';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Clock, XCircle } from 'lucide-react';

export default function LoginPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const email = searchParams.get('email');
  const approval = searchParams.get('approval');

  const { isLoading } = useAuth({
    middleware: 'guest',
    tmp: 'SignIn',
    redirectTo: '/monomo-ai',
  });

  const ssoFailureTracked = useRef(false);

  useEffect(() => {
    if (!ref) {
      return;
    }

    if (
      (ref === 'linkedin-cancelled' || ref === 'linkedin-error') &&
      !ssoFailureTracked.current
    ) {
      ssoFailureTracked.current = true;
      trackEvent('login_failed', { method: 'linkedin' });
    }

    if (
      (ref === 'google-cancelled' || ref === 'google-error') &&
      !ssoFailureTracked.current
    ) {
      ssoFailureTracked.current = true;
      trackEvent('login_failed', { method: 'google' });
    }

    const timerId = setTimeout(() => {
      if (ref === 'verified') {
        toast.success(t('verification.messages.success'), {
          duration: 5000,
          dismissible: true,
          id: 'verified',
        });
      }

      if (ref === 'email-changed') {
        toast.success(t('verification.messages.emailChanged'), {
          duration: 5000,
          dismissible: true,
          id: 'email-changed',
        });
      }

      if (ref === 'linkedin-cancelled') {
        toast.error(t('auth.linkedin.errors.cancelled'), {
          duration: 5000,
          dismissible: true,
          id: 'linkedin-cancelled',
        });
      }

      if (ref === 'linkedin-error') {
        toast.error(t('auth.linkedin.errors.configError'), {
          duration: 5000,
          dismissible: true,
          id: 'linkedin-error',
        });
      }

      if (ref === 'google-cancelled') {
        toast.error(t('auth.google.errors.cancelled'), {
          duration: 5000,
          dismissible: true,
          id: 'google-cancelled',
        });
      }

      if (ref === 'google-error') {
        toast.error(t('auth.google.errors.configError'), {
          duration: 5000,
          dismissible: true,
          id: 'google-error',
        });
      }

      const messages = {
        'invalid-token': t('verification.messages.invalidToken'),
        'already-verified': t('verification.messages.alreadyVerified'),
        'token-expired': t('verification.messages.tokenExpired'),
      };

      if (ref in messages) {
        toast.error(messages[ref as keyof typeof messages], {
          duration: 5000,
          dismissible: true,
          id: ref,
        });
      }
    });
    return () => clearTimeout(timerId);
  }, [ref, t]);

  if (isLoading) {
    return <Loading />;
  }

  const handleSuccess = () => void 0;

  const showApprovalPending = ref === 'verified' && approval === 'pending';
  const showApprovalRejected = ref === 'verified' && approval === 'rejected';

  return (
    <AuthLayout contentWidth="wide" hideFooter>
      <title>Přihlášení</title>
      <div className="mx-auto flex w-full max-w-md flex-col gap-4">
        {showApprovalPending ? (
          <Alert>
            <Clock className="size-4" />
            <AlertTitle>{t('verification.messages.approvalPendingTitle')}</AlertTitle>
            <AlertDescription>{t('verification.messages.approvalPendingDescription')}</AlertDescription>
          </Alert>
        ) : null}
        {showApprovalRejected ? (
          <Alert variant="destructive">
            <XCircle className="size-4" />
            <AlertTitle>{t('verification.messages.approvalRejectedTitle')}</AlertTitle>
            <AlertDescription>{t('verification.messages.approvalRejectedDescription')}</AlertDescription>
          </Alert>
        ) : null}
        <LoginForm email={email} onSuccess={handleSuccess} />
      </div>
    </AuthLayout>
  );
}
